import type { Chiave, Traduttore } from "../../i18n/index.ts";
import { useT } from "../lib/i18n.tsx";
import { useEffect, useMemo, useState } from "react";
// Riuso del parser del server: è codice puro, e condividerlo garantisce che
// interfaccia e motore interpretino un file .env esattamente allo stesso modo.
import {
  isSensitiveKey,
  parseEnvFile,
  serializeEnvFile,
} from "../../server/domain/env/env-file.ts";
import { CodeEditor } from "./CodeEditor.tsx";
import { Badge, Button, cx } from "./ui.tsx";

/**
 * Editor dell'environment, in due viste.
 *
 * La vista a righe risolve alla radice il problema dei valori che andavano a
 * capo: ogni valore vive in un campo di una riga sola, che scorre in orizzontale.
 * Un DSN lungo resta leggibile per quello che è — un valore unico — invece di
 * spezzarsi su tre righe come se fosse prosa.
 *
 * La vista testo resta disponibile perché incollare un blocco `.env` intero è
 * comodo, e togliere quella possibilità sarebbe un passo indietro.
 */

export interface EnvEditorProps {
  /** Valori correnti; il componente ne mantiene una copia modificabile. */
  value: Record<string, string>;
  onChange: (env: Record<string, string>) => void;
  readOnly?: boolean;
  /** Variabili che il compose richiede: serve a segnalare cosa manca. */
  richieste?: readonly string[];
  /** Il compose consegna l'intero environment: nessuna variabile è di troppo. */
  consegnaTutto?: boolean;
  /** Variabili assegnate da Portman, che non vanno inserite a mano. */
  gestite?: readonly string[];
  /** Variabili che nel compose hanno un valore di ripiego: non mancano. */
  conDefault?: readonly string[];
  /** Altezza in pixel dell'area scorrevole, per pareggiare l'editor accanto. */
  altezza?: number;
  /**
   * Variabile da aggiungere e mettere in evidenza appena il componente compare.
   *
   * Serve a chi arriva dalla verifica per impostarne una mancante: trovarsi la
   * riga già pronta è il motivo per cui ci si è arrivati.
   */
  daImpostare?: string;
}

type Vista = "righe" | "mancanti" | "testo";

interface Voce {
  /** Identità stabile: la chiave cambia mentre si digita, questa no. */
  readonly id: number;
  chiave: string;
  valore: string;
}

/** La maschera che il server invia al posto dei valori sensibili. */
const MASCHERA = "••••••••";

/**
 * Spazio sottratto all'altezza disponibile per le schede e il comando in fondo.
 *
 * Ora è una costante affidabile: finché le variabili mancanti stavano in fondo
 * come fila di pulsanti, l'ingombro dipendeva da quante fossero, e una stima
 * fissa produceva barre di scorrimento indesiderate.
 */
const PIEDE_CON_COMANDI = 84;
const PIEDE_SOLA_LETTURA = 44;

/** Ingombro del campo di filtro sopra l'elenco. */
const ALTEZZA_FILTRO = 38;

let contatore = 0;
const nuovaVoce = (chiave = "", valore = ""): Voce => ({ id: ++contatore, chiave, valore });

export function EnvEditor({
  value,
  onChange,
  readOnly = false,
  richieste = [],
  consegnaTutto = false,
  gestite = [],
  conDefault = [],
  altezza,
  daImpostare,
}: EnvEditorProps) {
  const [voci, setVoci] = useState<Voce[]>(() =>
    Object.entries(value).map(([chiave, valore]) => nuovaVoce(chiave, valore)),
  );
  const [vista, setVista] = useState<Vista>("righe");
  const [filtro, setFiltro] = useState("");
  const t = useT();
  // Il campo di filtro aggiunge una riga sopra l'elenco.
  const piede = (readOnly ? PIEDE_SOLA_LETTURA : PIEDE_CON_COMANDI) + ALTEZZA_FILTRO;
  const [rivelate, setRivelate] = useState<ReadonlySet<number>>(new Set());

  useEffect(() => {
    if (!daImpostare) return;

    setVista("righe");
    // Il filtro isola la riga: in un elenco lungo, aggiungerla in fondo senza
    // portarvi lo sguardo equivarrebbe a non averla aggiunta.
    setFiltro(daImpostare);

    setVoci((precedenti) =>
      precedenti.some((voce) => voce.chiave.trim() === daImpostare)
        ? precedenti
        : [...precedenti, nuovaVoce(daImpostare)],
    );
  }, [daImpostare]);

  /** Propaga al padre la mappa risultante, ignorando le righe senza chiave. */
  const aggiorna = (prossime: Voce[]) => {
    setVoci(prossime);

    const env: Record<string, string> = {};
    for (const voce of prossime) {
      const chiave = voce.chiave.trim();
      if (chiave.length > 0) env[chiave] = voce.valore;
    }

    onChange(env);
  };

  const presenti = useMemo(
    () => new Set(voci.map((v) => v.chiave.trim()).filter((c) => c.length > 0)),
    [voci],
  );

  /**
   * Voci corrispondenti al filtro.
   *
   * In un elenco, portare alla riga cercata significa mostrare quella e nascondere
   * il resto: è più diretto che evidenziarla in mezzo alle altre e scorrere.
   * Il confronto include il valore, perché a volte si cerca un indirizzo o una
   * porta e non si ricorda in quale variabile stia.
   */
  const filtrate = useMemo(() => {
    const termine = filtro.trim().toLowerCase();
    if (termine.length === 0) return voci;

    return voci.filter(
      (voce) =>
        voce.chiave.toLowerCase().includes(termine) ||
        voce.valore.toLowerCase().includes(termine),
    );
  }, [voci, filtro]);

  /**
   * Variabili che impedirebbero l'avvio: richieste dal compose, non impostate,
   * non assegnate da Portman e senza un valore di ripiego. Una variabile con
   * predefinito non manca, e segnalarla manderebbe a cercare un problema
   * che non esiste.
   */
  const mancanti = richieste.filter(
    (nome) => !presenti.has(nome) && !gestite.includes(nome) && !conDefault.includes(nome),
  );

  const intestazione = (
    <Schede
      vista={vista}
      onCambia={setVista}
      impostate={presenti.size}
      mancanti={mancanti.length}
    />
  );

  if (vista === "testo") {
    return (
      <div className="space-y-2">
        {intestazione}

        <TestoEnv
          iniziale={serializeEnvFile(
            Object.fromEntries(
              voci.filter((v) => v.chiave.trim()).map((v) => [v.chiave.trim(), v.valore]),
            ),
          )}
          onChange={(testo) => {
            const env = parseEnvFile(testo);
            aggiorna(Object.entries(env).map(([chiave, valore]) => nuovaVoce(chiave, valore)));
          }}
          linguaggio="env"
          readOnly={readOnly}
          righe={20}
          {...(altezza === undefined ? {} : { altezza: altezza - piede })}
          numeriDiRiga={false}
          placeholder="TAG=v1.0.0" // i18n-ignora
        />

        <p className="text-xs tenue">{t.t("env.testoAiuto")}</p>
      </div>
    );
  }

  if (vista === "mancanti") {
    return (
      <div className="space-y-2">
        {intestazione}

        <div
          className="superficie divide-y overflow-y-auto rounded-lg"
          style={altezza === undefined ? undefined : { maxHeight: `${altezza - piede}px` }}
        >
          {mancanti.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm tenue">{t.t("env.nessunaMancante")}</p>
          ) : (
            mancanti.map((nome) => (
              <div key={nome} className="flex items-center gap-3 px-3 py-2 text-sm">
                <code className="min-w-0 flex-1 truncate font-mono text-[13px] text-amber-700 dark:text-amber-400">
                  {nome}
                </code>

                {!readOnly && (
                  <Button
                    className="shrink-0 text-xs"
                    onClick={() => {
                      aggiorna([...voci, nuovaVoce(nome)]);
                      // Torna all'elenco: la variabile appena aggiunta va
                      // compilata, e restare qui la nasconderebbe.
                      setVista("righe");
                    }}
                  >
                    {t.t("verifica.imposta")}
                  </Button>
                )}
              </div>
            ))
          )}
        </div>

        <p className="text-xs tenue">{t.t("env.mancantiAiuto")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {intestazione}

      <div className="relative">
        <input
          value={filtro}
          onChange={(evento) => setFiltro(evento.target.value)}
          onKeyDown={(evento) => evento.key === "Escape" && setFiltro("")}
          placeholder={t.t("env.filtroSegnaposto")}
          spellCheck={false}
          aria-label={t.t("env.filtro")}
          className="w-full rounded-lg border bg-[var(--superficie)] py-1.5 pl-8 pr-16 text-[13px] placeholder:text-[var(--testo-tenue)] focus:outline-none"
        />

        <svg viewBox="0 0 24 24" className="pointer-events-none absolute left-2.5 top-2 size-4 tenue" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-4.5-4.5" strokeLinecap="round" />
        </svg>

        {filtro.trim().length > 0 && (
          <span className="absolute right-2.5 top-1.5 text-xs tenue">
            {t.t("env.filtrate", { n: filtrate.length, totale: voci.length })}
          </span>
        )}
      </div>

      {/* `@container` fa dipendere il layout delle righe dalla larghezza di
          questo pannello e non da quella della finestra: con il divisore, i due
          valori possono essere molto diversi. */}
      <div
        className="@container superficie divide-y overflow-y-auto rounded-lg"
        style={altezza === undefined ? undefined : { maxHeight: `${altezza - piede}px` }}
      >
        {voci.length === 0 && (
          <p className="px-3 py-6 text-center text-sm tenue">
            {t.t("storico.nessunaVariabile")} {!readOnly && t.t("env.aggiungineUna")}
          </p>
        )}

        {voci.length > 0 && filtrate.length === 0 && (
          <p className="px-3 py-6 text-center text-sm tenue">
            {t.t("env.nessunaCorrisponde", { filtro: filtro.trim() })}
          </p>
        )}

        {filtrate.map((voce) => (
          <RigaVariabile
            key={voce.id}
            voce={voce}
            readOnly={readOnly}
            richiesta={richieste.includes(voce.chiave.trim())}
            inutilizzata={
              !consegnaTutto &&
              richieste.length > 0 &&
              voce.chiave.trim().length > 0 &&
              !richieste.includes(voce.chiave.trim())
            }
            rivelata={rivelate.has(voce.id)}
            onRivela={() => setRivelate(new Set([...rivelate, voce.id]))}
            onModifica={(modifiche) =>
              aggiorna(voci.map((v) => (v.id === voce.id ? { ...v, ...modifiche } : v)))
            }
            onRimuovi={() => aggiorna(voci.filter((v) => v.id !== voce.id))}
          />
        ))}

        {/* Le porte che assegna Portman compaiono qui in sola lettura: sono nel
            compose come le altre, e non vederle affatto le farebbe sembrare
            dimenticate proprio a chi sta controllando che nulla manchi. */}
        {gestite
          .filter(
            (nome) =>
              !presenti.has(nome) &&
              (filtro.trim().length === 0 ||
                nome.toLowerCase().includes(filtro.trim().toLowerCase())),
          )
          .map((nome) => (
            <div key={nome} className="flex items-center gap-2 px-2.5 py-1.5">
              <span className="min-w-[6rem] flex-1 px-2 font-mono text-[13px] text-sky-700/70 @[34rem]:w-[13rem] @[34rem]:flex-none dark:text-sky-300/70">
                {nome}
              </span>
              <span className="tenue">=</span>
              <span className="min-w-0 flex-[2] truncate px-2 text-[13px] tenue">
                {t.t("env.assegnataAvvio")}
              </span>
              <span className="hidden shrink-0 whitespace-nowrap @[34rem]:inline-flex">
                <Badge tone="accent">{t.t("env.gestita")}</Badge>
              </span>
            </div>
          ))}
      </div>

      {!readOnly && (
        <Button className="text-xs" onClick={() => aggiorna([...voci, nuovaVoce()])}>
          {t.t("env.aggiungi")}
        </Button>
      )}
    </div>
  );
}

/**
 * Schede dell'environment.
 *
 * Le variabili mancanti stavano in fondo, come una fila di pulsanti che cresceva
 * col loro numero: l'altezza del pannello diventava imprevedibile e comparivano
 * barre di scorrimento indesiderate. Qui occupano una scheda, che ha sempre la
 * stessa altezza, e il conteggio resta visibile senza dover guardare in fondo.
 */
function Schede({
  vista,
  onCambia,
  impostate,
  mancanti,
}: {
  vista: Vista;
  onCambia: (vista: Vista) => void;
  impostate: number;
  mancanti: number;
}) {
  const t = useT();
  const voci: { nome: Vista; etichetta: Chiave; conteggio?: number; allerta?: boolean }[] = [
    { nome: "righe", etichetta: "storico.environment", conteggio: impostate },
    ...(mancanti > 0
      ? [{ nome: "mancanti" as Vista, etichetta: "env.mancanti" as const, conteggio: mancanti, allerta: true }]
      : []),
    { nome: "testo", etichetta: "env.testo" },
  ];

  return (
    <div className="flex items-center gap-1 text-sm">
      {voci.map((voce) => (
        <button
          key={voce.nome}
          onClick={() => onCambia(voce.nome)}
          className={cx(
            "rounded-md px-2.5 py-1 transition-colors",
            vista === voce.nome
              ? "bg-[var(--superficie-tenue)] font-medium"
              : "tenue hover:bg-[var(--superficie-tenue)]",
            voce.allerta && vista !== voce.nome && "text-amber-600 dark:text-amber-400",
          )}
        >
          {t.t(voce.etichetta)}
          {voce.conteggio !== undefined && (
            <span className={cx("ml-1.5", vista === voce.nome ? "tenue" : "")}>
              {voce.conteggio}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

/** Descrizione dello stato, per il titolo quando i contrassegni non stanno. */
function etichettaStato(richiesta: boolean, inutilizzata: boolean, t: Traduttore): string {
  if (inutilizzata) return t.t("env.statoInutilizzata");
  if (richiesta) return t.t("env.statoRichiesta");

  return "";
}

function RigaVariabile({
  voce,
  readOnly,
  richiesta,
  inutilizzata,
  rivelata,
  onRivela,
  onModifica,
  onRimuovi,
}: {
  voce: Voce;
  readOnly: boolean;
  richiesta: boolean;
  inutilizzata: boolean;
  rivelata: boolean;
  onRivela: () => void;
  onModifica: (modifiche: Partial<Voce>) => void;
  onRimuovi: () => void;
}) {
  const nascostoDalServer = voce.valore === MASCHERA;
  const daNascondere = isSensitiveKey(voce.chiave) && !rivelata && !nascostoDalServer;
  const t = useT();

  return (
    <div className="flex items-center gap-2 px-2.5 py-1.5">
      <input
        value={voce.chiave}
        onChange={(evento) => onModifica({ chiave: evento.target.value })}
        readOnly={readOnly}
        spellCheck={false}
        placeholder={t.t("env.chiave")}
        className={cx(
          // Cede larghezza quando il pannello è stretto, entro un minimo
          // leggibile: una chiave fissa lascerebbe al valore due caratteri.
          "min-w-[6rem] flex-1 rounded-md bg-transparent px-2 py-1 font-mono text-[13px]",
          "@[34rem]:max-w-[13rem] @[34rem]:flex-none @[34rem]:w-[13rem]",
          "text-sky-700 focus:bg-[var(--superficie-tenue)] focus:outline-none dark:text-sky-300",
        )}
      />

      <span className="tenue">=</span>

      {/* Il valore vive in un campo di una riga sola: scorre in orizzontale
          invece di andare a capo, ed è il punto dell'intera vista. */}
      <input
        value={daNascondere ? MASCHERA : voce.valore}
        onChange={(evento) => onModifica({ valore: evento.target.value })}
        readOnly={readOnly || daNascondere || nascostoDalServer}
        spellCheck={false}
        placeholder={t.t("verifica.segnaposto")}
        className={cx(
          "min-w-0 flex-[2] rounded-md bg-transparent px-2 py-1 font-mono text-[13px]",
          "focus:bg-[var(--superficie-tenue)] focus:outline-none",
        )}
      />

      <span className="hidden shrink-0 items-center gap-1.5 whitespace-nowrap @[34rem]:flex">
        {richiesta && <Badge tone="accent">{t.t("env.nelCompose")}</Badge>}
        {inutilizzata && <Badge>{t.t("env.nonUsata")}</Badge>}
        {nascostoDalServer && <Badge tone="warning">{t.t("env.nascosto")}</Badge>}
      </span>

      {/* Sotto quella larghezza resta un solo segno, che non va a capo. */}
      <span className="shrink-0 @[34rem]:hidden" title={etichettaStato(richiesta, inutilizzata, t)}>
        {inutilizzata && <span className="text-xs tenue">{t.t("env.nonUsata")}</span>}
      </span>

      {daNascondere && (
        <Button variant="ghost" className="shrink-0 px-2 text-xs" onClick={onRivela}>
          {t.t("comune.mostra")}
        </Button>
      )}

      {!readOnly && (
        <button
          onClick={onRimuovi}
          aria-label={t.t("env.rimuovi", { nome: voce.chiave || t.t("env.variabile") })}
          className="shrink-0 rounded-md px-2 py-1 text-sm tenue hover:bg-[var(--superficie-tenue)] hover:text-red-600"
        >
          ×
        </button>
      )}
    </div>
  );
}

/**
 * L'editor della vista testo, che conserva il testo così come lo si scrive.
 *
 * Rigenerarlo dalle variabili a ogni battuta cancellava le righe ancora a metà
 * — un a capo, una chiave senza `=` — e riordinava le altre sotto il cursore:
 * aggiungere una variabile in fondo era impossibile. Il testo si ricava dalle
 * variabili solo all'apertura della vista; poi va in un verso solo.
 */
function TestoEnv({
  iniziale,
  onChange,
  ...props
}: { iniziale: string; onChange: (testo: string) => void } & Omit<
  React.ComponentProps<typeof CodeEditor>,
  "value" | "onChange"
>) {
  const [testo, setTesto] = useState(iniziale);

  return (
    <CodeEditor
      {...props}
      value={testo}
      onChange={(nuovo) => {
        setTesto(nuovo);
        onChange(nuovo);
      }}
    />
  );
}
